import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {FeaturesEnum, ProfileEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useContractStatus, usePersonalData} from 'presentation/hooks';
import {formatAddress, formatText} from 'presentation/utils';
import {useRegistrationUpdateStatusStore} from 'presentation/pages/private/store/useRegistrationUpdateStatusStore';
import {PredefinedFavoritePlacesEnum} from 'domain/usecases';
import {
	AddressUpdateStatusEnum,
	AddressValidationReasonEnum,
	addressStatusToModalMode,
} from '../components/AddressUpdateModal/constants';
import {AddressUpdateModalMode} from '../components/AddressUpdateModal/types';
import {useGetRegistrationUpdateStatusQuery} from '../query/useGetRegistrationUpdateStatusQuery';
import {useGetAddressSuggestionQuery} from '../query/useGetAddressSuggestionQuery';
import {usePostRespondAddressSuggestionMutation} from '../query/usePostRespondAddressSuggestionMutation';
import {usePatchConfirmAddressMutation} from '../query/usePatchConfirmAddressMutation';
import {AddressFormV2Values} from 'presentation/components/ChangeData/Address/AddressFormV2/types';
import {usePostFavoriteAddressMutation} from 'presentation/pages/private/profile/FavoriteAddresses/mutations/usePostFavoriteAddressMutation';
import {useAccountDataStore} from '../../store/useAccountDataStateStore';
import {useAccountInformation} from '../../hooks';
import {useGetAddressUpdateIndicatorQuery} from '../query/useGetAddressUpdateIndicatorQuery';
import {useAddressUpdateModalDisplayStore} from '../store/useAddressUpdateModalDisplayStore';
import {PendingAddressUpdateAction, UseRegistrationUpdateSurfaceParams} from './types';

export function useRegistrationUpdateSurface({
	isAnyHomeModalOpen,
}: UseRegistrationUpdateSurfaceParams) {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const [isAddressUpdateModalOpen, setIsAddressUpdateModalOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<PendingAddressUpdateAction | null>(null);
	const {isEnabled: updatePersonalDataEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_UPDATE_PERSONAL_DATA,
	);
	const {isEnabled: isMultiAddressEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_MULTI_ADDRESS,
	);
	const {isEnabled: isNewProfileEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const {needsUpdate, setNeedsUpdate, isDrawerDismissed, dismissDrawer} =
		useRegistrationUpdateStatusStore();

	const {isHolder} = useAccountInformation();
	const {isValid} = useContractStatus();

	const {
		beneficiary: {cdUsuarioC, idadeC},
	} = useAccountDataStore(state => state.accountData);
	const hasAddressUpdateModalBeenShown = useAddressUpdateModalDisplayStore(
		state => !!cdUsuarioC && !!state.shownByBeneficiary[cdUsuarioC],
	);
	const markAddressUpdateModalAsShown = useAddressUpdateModalDisplayStore(
		state => state.markAsShown,
	);

	const canAccessMultiAddressFlow = isMultiAddressEnabled && isNewProfileEnabled && isValid;

	const isAdult = idadeC >= 18;
	const effectiveNeedsUpdate = needsUpdate;
	const effectiveIsDrawerDismissed = isDrawerDismissed;
	const isRegistrationUpdateStatusEnabled = isValid && updatePersonalDataEnabled;
	const isAddressSuggestionEnabled = canAccessMultiAddressFlow && !isHolder && isAdult;
	const isAddressUpdateIndicatorEnabled = canAccessMultiAddressFlow && isAdult;

	const {userLocation} = usePersonalData();

	const normalizeStreetType = (streetType?: string) => {
		const type = formatText(streetType ?? '')
			.replace(/\.+$/g, '')
			.trim();

		return type && `${type}.`;
	};

	const formattedAddress = formatAddress({
		type: normalizeStreetType(userLocation?.nmTipoLogradouro),
		address: userLocation?.nmRua,
		number: `${userLocation?.nuEndereco ?? ''}`,
		district: userLocation?.nmBairro,
		city: userLocation?.nmCidade,
		state: userLocation?.nmUf,
	});

	const formatModalAddress = (address?: {
		streetType?: string;
		street?: string;
		streetNumber?: string;
		district?: string;
		city?: string;
		state?: string;
	}) =>
		address
			? formatAddress({
					type: address.streetType,
					address: address.street,
					number: address.streetNumber,
					district: address.district,
					city: address.city,
					state: address.state,
				})
			: undefined;

	const {data: registrationUpdateStatus} = useGetRegistrationUpdateStatusQuery({
		enabled: isRegistrationUpdateStatusEnabled,
		beneficiaryCode: cdUsuarioC,
	});

	const {data: addressSuggestion} = useGetAddressSuggestionQuery({
		enabled: isAddressSuggestionEnabled,
		beneficiaryCode: cdUsuarioC,
	});

	const {data: addressUpdateIndicator} = useGetAddressUpdateIndicatorQuery({
		enabled: isAddressUpdateIndicatorEnabled,
		beneficiaryCode: cdUsuarioC,
	});

	const {modalMode, formattedSuggestionAddress} = useMemo((): {
		modalMode: AddressUpdateModalMode | undefined;
		formattedSuggestionAddress: string | undefined;
	} => {
		if (isAddressSuggestionEnabled && addressSuggestion) {
			return {
				modalMode: addressStatusToModalMode[AddressUpdateStatusEnum.Suggested],
				formattedSuggestionAddress: formatModalAddress(addressSuggestion),
			};
		}

		const shouldOpenModalIndicator =
			isAddressUpdateIndicatorEnabled && !!addressUpdateIndicator?.shouldAsk;

		if (!shouldOpenModalIndicator) {
			return {modalMode: undefined, formattedSuggestionAddress: undefined};
		}

		const indicatorReason = addressUpdateIndicator?.reason;
		const indicatorAddress = addressUpdateIndicator?.address;
		const shouldOpenRequestModal =
			indicatorReason === AddressValidationReasonEnum.DESATUALIZADO ||
			(indicatorReason === AddressValidationReasonEnum.SEM_ENDERECO_RESIDENCIA &&
				!indicatorAddress);

		if (shouldOpenRequestModal) {
			return {
				modalMode: addressStatusToModalMode[AddressUpdateStatusEnum.OutDated],
				formattedSuggestionAddress: undefined,
			};
		}

		if (indicatorReason === AddressValidationReasonEnum.QUALIDADE_INSUFICENTE) {
			return {
				modalMode: addressStatusToModalMode[AddressUpdateStatusEnum.InsufficientQualit],
				formattedSuggestionAddress: undefined,
			};
		}

		if (indicatorReason === AddressValidationReasonEnum.SEM_CONFIRMACAO_BENEFICIARIO) {
			return {
				modalMode: 'confirmation',
				formattedSuggestionAddress: undefined,
			};
		}

		const doesntHaveResidentialAddress =
			indicatorReason === AddressValidationReasonEnum.SEM_ENDERECO_RESIDENCIA &&
			!!indicatorAddress;

		if (doesntHaveResidentialAddress) {
			return {
				modalMode: addressStatusToModalMode[AddressUpdateStatusEnum.DoenstHaveResidential],
				formattedSuggestionAddress: formatModalAddress(indicatorAddress),
			};
		}

		return {modalMode: undefined, formattedSuggestionAddress: undefined};
	}, [
		addressSuggestion,
		addressUpdateIndicator,
		isAddressSuggestionEnabled,
		isAddressUpdateIndicatorEnabled,
	]);

	const {mutate: respondSuggestion} = usePostRespondAddressSuggestionMutation();
	const {mutate: confirmAddress} = usePatchConfirmAddressMutation();
	const {mutate: createFavoriteAddress} = usePostFavoriteAddressMutation();
	const isAddressUpdateActionPending = pendingAction !== null;

	useEffect(() => {
		if (!isRegistrationUpdateStatusEnabled) {
			setNeedsUpdate(false);
			return;
		}

		if (registrationUpdateStatus !== undefined) {
			setNeedsUpdate(registrationUpdateStatus.necessitaAtualizacao);
			return;
		}

		setNeedsUpdate(false);
	}, [
		cdUsuarioC,
		isRegistrationUpdateStatusEnabled,
		registrationUpdateStatus?.necessitaAtualizacao,
		setNeedsUpdate,
	]);

	useEffect(() => {
		setIsAddressUpdateModalOpen(false);
	}, [cdUsuarioC]);

	useEffect(() => {
		if (!modalMode || (effectiveNeedsUpdate && !effectiveIsDrawerDismissed)) {
			setIsAddressUpdateModalOpen(false);
			return;
		}

		if (isAddressUpdateModalOpen || hasAddressUpdateModalBeenShown) return;

		if (isAnyHomeModalOpen) return;

		markAddressUpdateModalAsShown(cdUsuarioC);
		setIsAddressUpdateModalOpen(true);
	}, [
		cdUsuarioC,
		effectiveIsDrawerDismissed,
		effectiveNeedsUpdate,
		hasAddressUpdateModalBeenShown,
		isAnyHomeModalOpen,
		isAddressUpdateModalOpen,
		markAddressUpdateModalAsShown,
		modalMode,
	]);

	const isRegistrationDataDrawerOpen = !!effectiveNeedsUpdate && !effectiveIsDrawerDismissed;

	const resetPendingAction = () => {
		setPendingAction(null);
	};

	const handleCloseAddressUpdateModal = () => {
		if (isAddressUpdateActionPending) return;
		setIsAddressUpdateModalOpen(false);
	};

	const handleNavigateToAddressForm = () => {
		navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
			state: {from: pathname, isComingFromUpdate: true},
		});
		handleCloseAddressUpdateModal();
	};

	const handleNavigateToAddressList = () => {
		navigate(ProfileEnum.FAVORITE_ADDRESSES_LIST);
		handleCloseAddressUpdateModal();
	};

	const handleNavigateToAddressFormWithCasa = () => {
		navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
			state: {from: pathname, item: {nmLocal: PredefinedFavoritePlacesEnum.HOUSE}},
		});
		handleCloseAddressUpdateModal();
	};

	const handleConfirmConfirmation = () => {
		const sequence = addressUpdateIndicator?.address?.sequence;
		if (!sequence || isAddressUpdateActionPending) return;

		setPendingAction('confirm-confirmation');
		confirmAddress(
			{sequence},
			{
				onSuccess: () => {
					resetPendingAction();
					handleCloseAddressUpdateModal();
				},
				onError: resetPendingAction,
			},
		);
	};

	const handleConfirmFirstAccess = () => {
		const indicatorAddress = addressUpdateIndicator?.address;
		if (!indicatorAddress || isAddressUpdateActionPending) return;

		const streetNumber = indicatorAddress.streetNumber;
		const hasWithoutNumber = !streetNumber || streetNumber === '0';
		const payload: AddressFormV2Values = {
			zipCode: indicatorAddress.zipCode,
			address: [indicatorAddress.streetType, indicatorAddress.street]
				.filter(Boolean)
				.join(' '),
			number: hasWithoutNumber ? '' : streetNumber,
			withoutNumber: hasWithoutNumber,
			complement: indicatorAddress.complement || '',
			district: indicatorAddress.district,
			city: indicatorAddress.city,
			state: indicatorAddress.state,
			favoriteAddress: PredefinedFavoritePlacesEnum.HOUSE,
			isMainAddress: true,
		};

		setPendingAction('confirm-first-access');
		createFavoriteAddress(
			{data: payload},
			{
				onSuccess: () => {
					resetPendingAction();
					handleCloseAddressUpdateModal();
				},
				onError: resetPendingAction,
			},
		);
	};

	const handleConfirmSuggestion = () => {
		if (!addressSuggestion || isAddressUpdateActionPending) return;

		setPendingAction('confirm-suggestion');
		respondSuggestion(
			{acceptedFlag: true, addressSuggestionId: addressSuggestion.id},
			{
				onSuccess: () => {
					resetPendingAction();
					handleCloseAddressUpdateModal();
				},
				onError: resetPendingAction,
			},
		);
	};

	const handleRejectSuggestion = () => {
		if (!addressSuggestion || isAddressUpdateActionPending) return;

		const indicatorReason = addressUpdateIndicator?.reason;
		const doesntHaveResidentialAddress =
			indicatorReason === AddressValidationReasonEnum.SEM_ENDERECO_RESIDENCIA;

		setPendingAction('reject-suggestion');
		respondSuggestion(
			{acceptedFlag: false, addressSuggestionId: addressSuggestion.id},
			{
				onSuccess: () => {
					resetPendingAction();
					handleCloseAddressUpdateModal();

					if (doesntHaveResidentialAddress) {
						navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
							state: {from: pathname, isComingFromUpdate: true},
						});
					}
				},
				onError: resetPendingAction,
			},
		);
	};

	return {
		modalMode,
		formattedAddress,
		formattedSuggestionAddress,
		isAddressUpdateModalOpen,
		isRegistrationDataDrawerOpen,
		dismissDrawer,
		handleCloseAddressUpdateModal,
		handleNavigateToAddressForm,
		handleNavigateToAddressList,
		handleNavigateToAddressFormWithCasa,
		handleConfirmConfirmation,
		handleConfirmFirstAccess,
		handleConfirmSuggestion,
		handleRejectSuggestion,
		isPrimaryActionLoading:
			pendingAction === 'confirm-confirmation' ||
			pendingAction === 'confirm-first-access' ||
			pendingAction === 'confirm-suggestion',
		isSecondaryActionLoading: pendingAction === 'reject-suggestion',
		isAddressUpdateActionPending,
	};
}
